import { jsx as _jsx } from "react/jsx-runtime";
/* eslint-disable consistent-return */
import React from 'react';
import styled, { css } from 'styled-components';
import { getSpacingStringFromDescription, } from 'web-app/styleguide/spacing';
import { media } from 'web-app/styleguide/utils';
import { breakPointToCSSFunction } from 'web-app/styleguide/breakpoint-helpers';
import { hasValue } from '@famly/stat_ts-utils_has-value';
import { elevationCSS } from 'web-app/styleguide/elevations';
const breakpoints = [
    'mobileLandscapeAndLarger',
    'tabletPortraitAndLarger',
    'tabletLandscapeAndLarger',
    'laptopAndLarger',
    'customAtLeastOneLargerThanLandscape',
];
const stringIsBreakpoint = (s) => breakpoints.includes(s);
const propsIsResponsive = (prop) => typeof prop === 'object' && Object.keys(prop).some(k => stringIsBreakpoint(k) || k === 'base');
export function stylePropToCSS(propsToStyleProp, cssProp) {
    return (props) => {
        const prop = propsToStyleProp(props);
        if (!hasValue(prop)) {
            return '';
        }
        if (propsIsResponsive(prop)) {
            return Object.keys(prop).map(breakpoint => {
                const breakpointValue = prop[breakpoint];
                if (!hasValue(breakpointValue)) {
                    return '';
                }
                if (stringIsBreakpoint(breakpoint)) {
                    return breakPointToCSSFunction(breakpoint) `${cssProp}: ${getSpacingStringFromDescription(breakpointValue, props.theme)};`;
                }
                else if (breakpoint === 'base') {
                    return css `
                        ${cssProp}: ${getSpacingStringFromDescription(breakpointValue, props.theme)};
                    `;
                }
            });
        }
        return css `
            ${cssProp}: ${getSpacingStringFromDescription(prop, props.theme)};
        `;
    };
}
// The unused props argument is there for type checking
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const marginPropsToCSS = (props) => css `
    ${stylePropToCSS(props => props.margin, 'margin')}
    ${stylePropToCSS(props => props.marginBottom, 'margin-bottom')}
    ${stylePropToCSS(props => props.marginTop, 'margin-top')}
    ${stylePropToCSS(props => props.marginRight, 'margin-right')}
    ${stylePropToCSS(props => props.marginLeft, 'margin-left')}
`;
/**
 * @deprecated Use the `Box` component from `@famly/mf_layout_box` instead
 */
export const Base = styled.div `
    ${marginPropsToCSS}
    ${stylePropToCSS(props => props.padding, 'padding')}
    ${stylePropToCSS(props => props.paddingBottom, 'padding-bottom')}
    ${stylePropToCSS(props => props.paddingTop, 'padding-top')}
    ${stylePropToCSS(props => props.paddingRight, 'padding-right')}
    ${stylePropToCSS(props => props.paddingLeft, 'padding-left')}
    ${stylePropToCSS(props => props.pointerEvents, 'pointer-events')}

    ${stylePropToCSS(props => (props.background ? props.theme.mf.colorPalette[props.background] : undefined), 'background')}
    ${stylePropToCSS(props => props.radius, 'border-radius')}

    ${elevationCSS}


    ${props => props.grow
    ? css `
                  flex-grow: ${props.grow};
              `
    : ''}
    ${props => props.shrink !== undefined
    ? css `
                  flex-shrink: ${props.shrink};
              `
    : ''}

    ${stylePropToCSS(props => props.basis, 'flex-basis')}

    ${stylePropToCSS(props => props.textAlign, 'text-align')}
    ${props => props.whiteSpace
    ? css `
                  white-space: ${props.whiteSpace};
              `
    : ''}
    ${props => props.alignSelf
    ? css `
                  align-self: ${props.alignSelf};
              `
    : ''}

    ${props => (props.css ? props.css : '')}

    ${props => props.fullWidth
    ? css `
                  width: 100%;
              `
    : ''}
    ${props => props.fullHeight
    ? css `
                  height: 100%;
              `
    : ''}

    ${stylePropToCSS(props => props.height, 'height')}

    ${stylePropToCSS(props => props.maxHeight, 'max-height')}

    ${stylePropToCSS(props => props.overflow, 'overflow')}

    ${props => props.flex
    ? css `
                  flex: ${props.flex};
              `
    : ''}

    ${props => props.visibility
    ? css `
                  visibility: ${props.visibility};
              `
    : ''}

    ${props => props.ellipsis
    ? css `
                  text-overflow: ellipsis;
              `
    : ''}

    ${props => props.relative
    ? css `
                  position: relative;
              `
    : ''}

    ${props => props.zIndex
    ? css `
                  z-index: ${props.zIndex};
              `
    : ''}

    ${props => props.boxSizing
    ? css `
                  box-sizing: ${props.boxSizing};
              `
    : ''}

    ${props => props.position
    ? css `
                  position: ${props.position};
              `
    : ''}

    ${props => hasValue(props.opacity)
    ? css `
                  opacity: ${props.opacity};
              `
    : ''}

    ${props => hasValue(props.minWidth)
    ? css `
                  min-width: ${formatMaybeNumber(props.minWidth, 'px')};
              `
    : ''}

    ${props => hasValue(props.minHeight)
    ? css `
                  min-height: ${formatMaybeNumber(props.minHeight, 'px')};
              `
    : ''}

    ${props => hasValue(props.maxWidth)
    ? css `
                  max-width: ${formatMaybeNumber(props.maxWidth, 'px')};
              `
    : ''}

    ${props => hasValue(props.cursor)
    ? css `
                  cursor: ${props.cursor};
              `
    : ''}

    ${props => (props.hideOnMobile ? media.mobile `display: none;` : '')}
    ${props => (props.showOnMobile ? media.notMobile `display: none;` : '')}
`;
const formatMaybeNumber = (value, postfix) => typeof value === 'number' ? `${value}${postfix}` : value;
export const InlineBase = styled(({ ref, ...rest }) => (_jsx(Base, { as: "span", ref: ref, ...rest }))) ``;
/**
 * @deprecated Use the `Stack` component from `@famly/mf_layout_stack` instead
 */
export const Flex = styled(Base) `
    display: flex;
    ${stylePropToCSS(props => props.justify, 'justify-content')}
    ${stylePropToCSS(props => props.wrap, 'flex-wrap')}
    ${stylePropToCSS(props => props.align, 'align-items')}
    ${stylePropToCSS(props => props.direction, 'flex-direction')}

    ${props => props.childSpacing &&
    css `
            & > * {
                :not(:last-child) {
                    ${props.direction === 'column' ? 'margin-bottom' : 'margin-right'}: ${getSpacingStringFromDescription(props.childSpacing, props.theme)};
                }
            }
        `}
`;
export const InlineFlex = styled(Flex) `
    display: inline-flex;
`;
